
import React from 'react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import FadeInSection from '@/components/ui/FadeInSection';
import GlassCard from '@/components/ui/GlassCard';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  Key, 
  Lock, 
  UserCheck, 
  Users, 
  Globe, 
  Cloud, 
  Check, 
  Fingerprint, 
  BrainCircuit, 
  Scale, 
  BarChart3, 
  Clock,
  Database,
  ServerCrash,
  AlertTriangle,
  Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/components/i18n/language-provider';

const Workforce = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-6">
          {/* Hero Section */}
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="inline-block rounded-full px-3 py-1 text-sm bg-brand-pink/10 text-brand-pink font-medium mb-4">
                {t("workforce.title")}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t("workforce.hero.title")}</h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto">
                {t("workforce.hero.description")}
              </p>
            </div>
          </FadeInSection>

          {/* Introduction */}
          <FadeInSection delay={100}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-20">
              <div>
                <h2 className="text-3xl font-bold mb-6">{t("workforce.intro.title")}</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  {t("workforce.intro.description")}
                </p>
                <Button 
                  className="bg-brand-pink hover:bg-brand-pink/90 text-white"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  {t("workforce.getStarted")}
                </Button>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/lovable-uploads/3b51e141-1044-414a-a93a-0387556f817d.png" 
                  alt={t("workforce.intro.imageAlt")}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  width="600" 
                  height="400"
                />
              </div>
            </div>
          </FadeInSection>

          {/* Key Features Section */}
          <FadeInSection delay={200}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("workforce.features.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("workforce.features.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Authentication */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <Key className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.features.authentication.title")}</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authentication.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authentication.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authentication.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authentication.item4")}</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* SSO */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <UserCheck className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.features.sso.title")}</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.sso.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.sso.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.sso.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.sso.item4")}</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* SCIM */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <Users className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.features.scim.title")}</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.scim.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.scim.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.scim.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.scim.item4")}</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* Dynamic Authorization */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <Lock className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.features.authorization.title")}</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authorization.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authorization.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authorization.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-brand-pink mr-2 mt-1">•</span>
                      <span>{t("workforce.features.authorization.item4")}</span>
                    </li>
                  </ul>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Add-ons Section */}
          <FadeInSection delay={300}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("workforce.addons.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("workforce.addons.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Real-time Detection */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <BrainCircuit className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.addons.ai.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("workforce.addons.ai.description")}
                  </p>
                </GlassCard>

                {/* MFA Enterprise */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <Fingerprint className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.addons.mfa.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("workforce.addons.mfa.description")}
                  </p>
                </GlassCard>

                {/* Risk-based Authentication */}
                <GlassCard className="p-6">
                  <div className="text-brand-pink mb-4">
                    <AlertTriangle className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("workforce.addons.threat.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("workforce.addons.threat.description")}
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Use Cases */}
          <FadeInSection delay={400}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("workforce.useCases.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("workforce.useCases.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Global Workforce Authentication */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-8 shadow-lg">
                  <div className="text-brand-pink mb-4">
                    <Globe className="h-12 w-12" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{t("workforce.useCases.global.title")}</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.global.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.global.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.global.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.global.item4")}</span>
                    </li>
                  </ul>
                </div>

                {/* Corporate SSO Deployment */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-8 shadow-lg">
                  <div className="text-brand-pink mb-4">
                    <Cloud className="h-12 w-12" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{t("workforce.useCases.corporate.title")}</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.corporate.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.corporate.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.corporate.item3")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-pink mr-2 mt-0.5 flex-shrink-0" />
                      <span>{t("workforce.useCases.corporate.item4")}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Capabilities */}
          <FadeInSection delay={500}>
            <div className="mb-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("workforce.capabilities.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("workforce.capabilities.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Performance and Scalability */}
                <GlassCard className="p-8">
                  <h3 className="text-2xl font-semibold mb-6 flex items-center">
                    <BarChart3 className="h-7 w-7 text-brand-pink mr-3" />
                    {t("workforce.capabilities.performance.title")}
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <Clock className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.performance.latency.title")}:</span>
                        <p className="text-muted-foreground">{t("workforce.capabilities.performance.latency.description")}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <ServerCrash className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.performance.availability.title")}:</span>
                        <p className="text-muted-foreground">{t("workforce.capabilities.performance.availability.description")}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <Globe className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.performance.residency.title")}:</span>
                        <p className="text-muted-foreground">{t("workforce.capabilities.performance.residency.description")}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <Database className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.performance.database.title")}:</span>
                        <p className="text-muted-foreground">{t("workforce.capabilities.performance.database.description")}</p>
                      </div>
                    </li>
                  </ul>
                </GlassCard>

                {/* Compliance */}
                <GlassCard className="p-8">
                  <h3 className="text-2xl font-semibold mb-6 flex items-center">
                    <Scale className="h-7 w-7 text-brand-pink mr-3" />
                    {t("workforce.capabilities.compliance.title")}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.compliance.gdpr.title")}:</span>
                        <p className="text-muted-foreground mb-4">{t("workforce.capabilities.compliance.gdpr.description")}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.compliance.hipaa.title")}:</span>
                        <p className="text-muted-foreground mb-4">{t("workforce.capabilities.compliance.hipaa.description")}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.compliance.lgpd.title")}:</span>
                        <p className="text-muted-foreground mb-4">{t("workforce.capabilities.compliance.lgpd.description")}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-brand-pink mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{t("workforce.capabilities.compliance.custom.title")}:</span>
                        <p className="text-muted-foreground">{t("workforce.capabilities.compliance.custom.description")}</p>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* CTA Section */}
          <FadeInSection delay={600}>
            <div className="text-center py-16 px-6 bg-gradient-to-r from-brand-pink/10 to-transparent rounded-2xl">
              <h2 className="text-3xl font-bold mb-4">{t("workforce.cta.title")}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                {t("workforce.cta.description")}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  className="bg-brand-pink hover:bg-brand-pink/90 text-white"
                  size="lg"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  {t("workforce.getStarted")}
                </Button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Workforce;
